import React, { useState, useEffect } from "react";
import { useStoreState } from "ezStore";
import moment from "moment";
import { gql } from "__generated__/gql";
import { intl } from "LocaleWrapper";

import { useLazyQuery, useMutation } from "@apollo/client";
import { FormGroup, Table, Button } from "@cofano/cck";
import { Popover, List, ListItem, ListItemText, Tabs, Tab, Box, Grid, CircularProgress } from "@mui/material";

import { Language, DataFormat, hasPluginPermission, usePermission } from "imports/Utils";

import { FormattedDate } from "utils/DateUtils";

import Revenue from "./Revenue.jsx";

const QUERY = gql(`#graphql
	query declarationReports($voyageIds: [Long]!, $shipId: Long!, $from: Instant!, $to: Instant!) {
		declarationReports(voyageIds: $voyageIds, shipId: $shipId, from: $from, to: $to) {
			voyageLogs {
				id
				voyageNumber
				visitedPorts(from: $from, to: $to) {
					id
					location {
						id
						description
						code
						displayCode
					}
					ship {
						id
						name
					}
					enteredDateTime
					exitedDateTime
				}
			}
			sailingReport {
				id
				bunkered
				bunkeringAfter
				bunkeringBefore
				fromTime
				toTime
				remark
				sailMinutes
				usageAmount
				usagePerHour
			}
			gasOilPrice
		}
	}
`);

const GENERATE_GASOIL_COSTS_MUTATION = gql(`#graphql
	mutation generateGasOilCosts($declarationId: Long, $sailingReportId: Long!) {
		generateGasOilCosts(declarationId: $declarationId, sailingReportId: $sailingReportId)
	}
`);

export const DECLARE_MUTATION = gql(`#graphql
	mutation declare($input: DeclareInput!) {
		declare(declareInput: $input) {
			id
		}
	}
`);

const TABS = {
	REVENUE: "summary",
	BUNKER: "bunker",
	PORTS: "ports",
	CALLS: "calls",
};


const Reports = ({ charter, window, refetch }) => {

	const [ reports, setReports ] = useState();
	const [ selectedTab, selectTab ] = useState(TABS.REVENUE);
	const ship = charter.ships?.[0];

	const [ getDeclarationReports, { loading } ] = useLazyQuery(QUERY, {
		onCompleted: data => {
			setReports(data.declarationReports);
		},
	});

	const [ declare ] = useMutation(DECLARE_MUTATION, {
		variables: {
			input: {
				id: charter?.declarations?.[0]?.id,
				charterId: charter.id,
				from: moment(window?.from).unix() * 1000,
				to: moment(window?.to).unix() * 1000,
				shipId: ship?.id,
			},
		},
	});

	const [ generateGasOilCosts ] = useMutation(GENERATE_GASOIL_COSTS_MUTATION);

	const saveGasOilLineWithDeclaration = async() => {
		let declarationId;
		if (charter?.declarations?.[0]?.id) {
			declarationId = charter.declarations[0].id;
		} else {
			await declare().then(response => {
				declarationId = response.data.declare.id;
			});
		}
		generateGasOilCosts({
			variables: {
				declarationId: declarationId,
				sailingReportId: reports?.sailingReport?.id,
			},
		}).then(() => refetch());
	};

	useEffect(() => {
		getDeclarationReports({
			variables: {
				voyageIds: charter.voyages.map(voyage => voyage.id),
				shipId: ship?.id,
				from: moment(window.from).unix() * 1000,
				to: moment(window.to).unix() * 1000,
			},
		});
	}, [ charter?.id, window.from, window.to ]);

	return (
		<div className="sailing-reports">
			{(loading || !reports) ? (
				<CircularProgress size={24} />
			) : (
				<React.Fragment>
					<Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 1 }}>
						<Tabs
							value={selectedTab}
							onChange={(e, value) => selectTab(value)}
							indicatorColor="primary">
							<Tab value={TABS.REVENUE} label={Language("REVENUE")} />
							<Tab value={TABS.BUNKER} label={Language("BUNKER")} />
							<Tab value={TABS.PORTS} label={Language("PORTS")} />
							<Tab value={TABS.CALLS} label={Language("CALLS")} />
						</Tabs>
					</Box>

					{selectedTab === TABS.REVENUE && (
						<Revenue
							charter={charter}
							refetch={refetch} />
					)}

					{selectedTab === TABS.BUNKER && (
						<React.Fragment>
							<Bunker
								report={reports}
								declarationWindow={window}
								ship={ship}
								generateGasOilCosts={saveGasOilLineWithDeclaration} />
						</React.Fragment>
					)}

					{selectedTab === TABS.PORTS && (
						<Ports reports={reports} />
					)}

					{selectedTab === TABS.CALLS && (
						<Calls voyages={charter.voyages} />
					)}
				</React.Fragment>
			)}
		</div>
	);
};

const Bunker = ({ report, declarationWindow, ship, generateGasOilCosts }) => {
	const sailingReport = report.sailingReport;
	const gasOilPrice = report.gasOilPrice;
	const { isReadOnly } = usePermission();

	return (
		<React.Fragment>
			<Grid spacing={4} container>
				<Grid xs={12} item>
					<FormGroup label={Language("SAILINGREPORT_SAILMINUTES")}
						labelPlacement="left-center"
						labelWidth={250}
						overflow="hidden">
						{sailingReport?.sailMinutes ? +(Number(sailingReport.sailMinutes) / 60).toFixed(2) + ` ${intl.formatMessage({ id: "HOURS" })}` : "-"}
					</FormGroup>

					<FormGroup label={Language("BUNKERING_BEFORE")}
						labelPlacement="left-center"
						labelWidth={250}
						overflow="hidden">
						{DataFormat.volumeL(sailingReport?.bunkeringBefore) || "-"}
					</FormGroup>

					<FormGroup label={Language("BUNKERED")}
						labelPlacement="left-center"
						labelWidth={250}
						overflow="hidden">
						{DataFormat.volumeL(sailingReport?.bunkered) || "-"}
					</FormGroup>

					<FormGroup label={Language("BUNKERING_AFTER")}
						labelPlacement="left-center"
						labelWidth={250}
						overflow="hidden">
						{DataFormat.volumeL(sailingReport?.bunkeringAfter) || "-"}
					</FormGroup>

					<FormGroup label={Language("USAGE_AMOUNT")}
						labelPlacement="left-center"
						labelWidth={250}
						overflow="hidden">
						{DataFormat.volumeL(sailingReport?.usageAmount) || "-"}
					</FormGroup>

					<FormGroup label={Language("USAGE_PER_HOUR")}
						labelPlacement="left-center"
						labelWidth={250}
						overflow="hidden">
						{DataFormat.volumeL(sailingReport?.usagePerHour) || "-"}
					</FormGroup>

					<FormGroup label={Language("GASOIL_PRICE")}
						labelPlacement="left-center"
						labelWidth={250}
						overflow="hidden">
						{DataFormat.price(gasOilPrice)}
					</FormGroup>
				</Grid>
			</Grid>
			{!isReadOnly && <div className="sailing-report-btns">
				<Button
					disabled={!sailingReport?.id}
					onClick={() => generateGasOilCosts()}
					variant="outlined"
					size="small">
					{Language("GENERATE_GASOIL_COSTS")}
				</Button>

				<Button
					onClick={() => {
						if (sailingReport?.id) {
							window.open(`/view/sailingreports/${sailingReport.id}/details`);
						} else {
							window.open(
								`/view/sailingreports/create
									?from=${moment(declarationWindow?.from).unix() * 1000}
									&to=${moment(declarationWindow?.to).unix() * 1000}
									&shipId=${ship.id}
									&shipName=${encodeURIComponent(ship.name)}`,
								"_blank",
							);
						}
					}}
					variant="outlined"
					size="small">
					{Language("SAILING_REPORT")}
				</Button>
			</div>}
		</React.Fragment>
	);
};

const Ports = ({ reports }) => {

	const voyages = [ ...reports.voyageLogs ].flatMap(v => v);
	const ports = [ ...voyages ].flatMap(v => v.visitedPorts || []);
	const ids = ports.map(o => o.id);
	const filtered = [ ...ports ].filter(({ id }, index) => !ids.includes(id, index + 1));

	return (
		<Table
			id="declaration-ports"
			disableFooter={true}
			defaultSize="COMPACT"
			content={filtered}
			columns={[ {
				id: "location",
				title: intl.formatMessage({ id: "PORT" }),
				render: (row, field) => field?.code || field?.description,
			}, {
				id: "enteredDateTime",
				title: intl.formatMessage({ id: "SHIP_LOG_ENTERED_DATETIME" }),
				render: (row) => <FormattedDate date={row.enteredDateTime} withoutYear />,
			}, {
				id: "exitedDateTime",
				title: intl.formatMessage({ id: "SHIP_LOG_EXITED_DATETIME" }),
				render: (row) => <FormattedDate date={row.exitedDateTime} withoutYear />,
			}, {
				id: "timeInPort",
				title: intl.formatMessage({ id: "SHIP_LOG_TIME_IN_PORT" }),
				render: (row) => {
					if (row.enteredDateTime && row.exitedDateTime) {
						const minutes = moment(row.exitedDateTime).diff(moment(row.enteredDateTime), "minutes");
						return DataFormat.minutesToTime(minutes);
					}
				},
			} ]} />
	);
};

const Calls = ({ voyages }) => {

	const session = useStoreState(store => store.session);

	const [ state, setState ] = useState({
		anchorEl: null,
		comments: [],
	});

	const calls = [ ...voyages ].flatMap(v => v.calls.map(c => {
		return {
			...c,
			voyageNumber: v.voyageNumber,
		};
	}));

	const usesContainers = hasPluginPermission(session, "useContainers");

	const open = Boolean(state.anchorEl);
	const id = open ? "simple-popover" : undefined;

	return (
		<React.Fragment>
			<Table
				id="declaration-calls"
				disableFooter={true}
				defaultSize="COMPACT"
				content={calls}
				columns={[ {
					id: "voyageNumber",
					title: intl.formatMessage({ id: "VOYAGE" }),
					width: 150,
				}, {
					id: "location.code",
					title: intl.formatMessage({ id: "LOCATION" }),
					render: (row) => row.location?.displayCode || row.location?.code || "",
				}, {
					id: "pta",
					title: intl.formatMessage({ id: "PTA" }),
					render: (row, field) => <FormattedDate date={field} format="ll" />,
				}, {
					id: "weight",
					title: `L/D (${intl.formatMessage({ id: usesContainers ? "UNIT_TON" : "WEIGHT" })})`,
					render: (row) => `${row.loadedWeight || 0}/${row.dischargedWeight || 0}`,
				}, {
					id: "teu",
					title: `L/D (${intl.formatMessage({ id: "TEU" })})`,
					render: (row) => `${row.callLoadingTeu || 0}/${row.callDischargingTeu || 0}`,
				}, {
					id: "comments",
					title: intl.formatMessage({ id: "COMMENT" }),
					render: (row, field) => {
						return (
							<a
								onClick={(event) => {
									setState({
										comments: field,
										anchorEl: event.currentTarget,
									});
								}}>
								{field?.[field.length - 1]?.comment}
							</a>
						);
					},
				} ]} />

			<Popover
				id={id}
				open={open}
				anchorEl={state.anchorEl}
				onClose={() => setState({
					anchorEl: null,
					comments: [],
				})}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
				transformOrigin={{ vertical: "top", horizontal: "center" }}>
				<div style={{ width: 300, padding: 4 }}>
					<List dense={true}>
						{state.comments.map((item, index) => {
							return (
								<ListItem key={index}>
									<ListItemText
										primary={item.comment}
										secondary={(
											<React.Fragment>
												{`${item.createdBy.username} - `}
												<FormattedDate date={item.created} withoutYear />
											</React.Fragment>
										)} />
								</ListItem>
							);
						})}
					</List>
				</div>
			</Popover>
		</React.Fragment>
	);
};

export default Reports;
